import React, { useEffect, useReducer } from 'react';
import { Button, Typography } from '@mui/material';
import { Restore, Save } from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { RADIUS_PERMISSION_GROUPS, VARIANTS } from 'shared/constants';
import { useAdminRadUserGroups, useRequest } from 'shared/hooks';
import {
  createAdminRadCheck,
  createAdminRadiusUserGroup,
  deleteAdminRadiusUserGroup,
  getAdminRadCheckByUsername,
  updateAdminRadCheck,
} from 'shared/api/admin-radius';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { TextEditor } from 'shared/components/data-display/editors';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { GridWrapper } from 'shared/components/data-display/GridFormatter';
import AdminRadUserGroupTable from './AdminRadUserGroupTable';

/**
 * @param {Object} props
 * @param {Object} props.state
 * @param {boolean} props.isProfile
 */
export default function RadiusPermissions(props) {
  const { state: { data: user }, isProfile } = props;
  const { radUserGroups: currentGroups, setRadUserGroups } = useAdminRadUserGroups(user?.username);
  // When in profile routes, only show added permissions.
  // When in admin routes, show all permissions + checkboxes.
  const allGroups = isProfile
    ? RADIUS_PERMISSION_GROUPS.filter(
      (rpg) => currentGroups.find(
        (addedRpg) => addedRpg.groupname === rpg.groupname,
      ) !== undefined,
    )
    : RADIUS_PERMISSION_GROUPS;
  const radUserGroupTableState = { currentGroups, allGroups };

  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState, undefined);
  const { data: radCheck, updatedData, dirty } = state;

  const { request } = useRequest();

  useEffect(() => {
    if (!user) return;

    request(getAdminRadCheckByUsername(user.username), '', false)
      .then((res) => {
        dispatch({ action: DataReducerActions.set, payload: res });
      });
  }, [request, user]);

  const handleSaveTenantRadCheck = async () => {
    if (!updatedData.value) return;

    const body = {
      username: user.username,
      value: updatedData.value,
    };

    let res;
    if (radCheck?.id) {
      res = await request(updateAdminRadCheck(body, radCheck.id), 'Updated Radius credentials');
    } else {
      res = await request(createAdminRadCheck(body), 'Created Radius credentials');
    }

    dispatch({ action: DataReducerActions.set, payload: res });
  };

  const handleChangeRadUserGroup = async (groupname) => {
    // Determine if the group should be either created or removed.
    const create = !(currentGroups.find((g) => g.groupname === groupname) !== undefined);

    const body = {
      username: user.username,
      groupname,
    };
    const req = create ? createAdminRadiusUserGroup : deleteAdminRadiusUserGroup;
    const res = await request(req(body), create ? `Added rad user group ${groupname}` : `Removed rad user group ${groupname}`);

    if (create) {
      setRadUserGroups(currentGroups.concat(res));
    } else {
      setRadUserGroups(currentGroups.filter((ag) => ag.groupname !== groupname));
    }
  };

  const handleRevert = () => {
    dispatch({ action: DataReducerActions.clear });
  };

  const handleChangePass = (key, value) => {
    dispatch({
      action: DataReducerActions.update,
      payload: { key, value },
    });
  };

  const descriptions = [{
    label: 'Radius Password',
    key: 'value',
    editor: TextEditor,
    onFocus: () => {
      if (updatedData.value) return;
      dispatch({ action: DataReducerActions.update, payload: { key: 'value', value: '' } });
    },
    onBlur: () => {
      if (dirty && updatedData.value) return;
      dispatch({ action: DataReducerActions.clear });
    },
  }];

  return (
    <Grid container>
      <GridWrapper title="Radius Permissions" size={12}>
        {isProfile && !radCheck?.id ? (
          <Typography sx={{ fontStyle: 'italic' }}>Radius credentials can be created in the Admin panel</Typography>
        ) : (
          <Grid
            container
            size={12}
            spacing={1}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid size={9}>
              <DataFormatter
                data={radCheck}
                editing
                updateValue={handleChangePass}
                descriptions={descriptions}
                updatedData={updatedData}
              />
            </Grid>
            <Grid container>
              { dirty && !!radCheck.id && (
              <Grid>
                <Button
                  startIcon={<Restore />}
                  onClick={handleRevert}
                  variant={VARIANTS.outlined}
                >
                  Clear
                </Button>
              </Grid>
              )}
              <Grid>
                <Button
                  disabled={!updatedData.value}
                  startIcon={<Save />}
                  variant={VARIANTS.contained}
                  onClick={handleSaveTenantRadCheck}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}
      </GridWrapper>
      <Grid size={12}>
        <AdminRadUserGroupTable
          state={radUserGroupTableState}
          onChangeRadUserGroup={handleChangeRadUserGroup}
          disabled={!radCheck?.id}
          isProfile={isProfile}
        />
      </Grid>
    </Grid>
  );
}
